import { type Editor } from '@tiptap/core';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import {
  Bold,
  Code,
  Code2,
  Italic,
  Link as LinkIcon,
  List,
  ListChecks,
  ListOrdered,
  type LucideIcon,
  Minus,
  Quote,
  Table as TableIcon,
} from 'lucide-react';

type CommandItem = {
  label: string;
  title: string;
  onClickAction: () => void;
  disabled: boolean;
  isActive?: boolean;
};

type CommandSection = {
  label: string;
  items: CommandItem[];
};

type IconCommandItem = CommandItem & {
  icon: LucideIcon;
};

type IconCommandSection = {
  items: IconCommandItem[];
};

type HeadingLevel = 0 | 1 | 2 | 3 | 4;

type HeadingOption = {
  label: string;
  level: HeadingLevel;
  isActive: boolean;
  onClickAction: () => void;
};

type HeadingDropdown = {
  currentLabel: string;
  options: HeadingOption[];
  disabled: boolean;
};

type SharedToolbar = {
  heading: HeadingDropdown;
  divider: IconCommandItem;
  inline: IconCommandSection;
  list: IconCommandSection;
  block: IconCommandSection;
  link: IconCommandItem;
};

export type ConventionTiptapCommands = SharedToolbar & {
  variant: 'convention';
  markers: CommandSection;
};

export type GeneralTiptapCommands = SharedToolbar & {
  variant: 'general';
};

export type TiptapCommands = ConventionTiptapCommands | GeneralTiptapCommands;

type Vocabulary = {
  headingLabels: Record<HeadingLevel, string>;
  headingLevels: HeadingLevel[];
  inlineTitles: { bold: string; italic: string; code: string };
  listTitles: { bullet: string; numbered: string; checklist: string };
  blockTitles: { quote: string; codeBlock: string; table: string; divider: string };
  linkTitle: string;
};

const CONVENTION_VOCAB: Vocabulary = {
  headingLabels: {
    0: 'Normal',
    1: 'Rule Title',
    2: 'Sub Rule',
    3: 'Rule Detail',
    4: 'Nested Item',
  },
  headingLevels: [0, 1, 2, 3, 4],
  inlineTitles: {
    bold: 'Key Highlight - highlight key terms',
    italic: 'Supplement - supplementary text',
    code: 'Code Ref - variable/function refs',
  },
  listTitles: {
    bullet: 'Rule List - list rules',
    numbered: 'Steps - ordered steps',
    checklist: 'Checklist - verification items',
  },
  blockTitles: {
    quote: 'Note - notes/warnings',
    codeBlock: 'Code Example - code samples',
    table: 'Reference Table - reference details',
    divider: 'Horizontal rule - section divider',
  },
  linkTitle: 'Related Doc - connect related docs',
};

const GENERAL_VOCAB: Vocabulary = {
  headingLabels: {
    0: 'Normal',
    1: 'Heading 1',
    2: 'Heading 2',
    3: 'Heading 3',
    4: 'Heading 3',
  },
  headingLevels: [0, 1, 2, 3],
  inlineTitles: {
    bold: 'Bold (⌘B)',
    italic: 'Italic (⌘I)',
    code: 'Inline code (⌘E)',
  },
  listTitles: {
    bullet: 'Bullet list',
    numbered: 'Numbered list',
    checklist: 'Checklist',
  },
  blockTitles: {
    quote: 'Quote',
    codeBlock: 'Code block',
    table: 'Insert table',
    divider: 'Horizontal rule',
  },
  linkTitle: 'Link (⌘K)',
};

export const useTiptapCommands = (
  editor: Editor | null,
  variant: 'convention' | 'general' = 'convention',
): TiptapCommands => {
  const t = useTranslations('components.tiptapEditor');

  const setLink = useCallback(() => {
    const url = window.prompt(t('promptUrl'), t('promptDefaultUrl'));
    if (!editor || url === null) {
      return;
    }
    if (!url.trim()) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url.trim() }).run();
  }, [editor, t]);

  const vocab = variant === 'convention' ? CONVENTION_VOCAB : GENERAL_VOCAB;

  const activeHeadingLevel: HeadingLevel = editor?.isActive('heading', { level: 1 })
    ? 1
    : editor?.isActive('heading', { level: 2 })
      ? 2
      : editor?.isActive('heading', { level: 3 })
        ? 3
        : editor?.isActive('heading', { level: 4 })
          ? 4
          : 0;

  const buildHeadingOnClick = (level: HeadingLevel) => () => {
    if (!editor) return;
    if (level === 0) {
      editor.chain().focus().setParagraph().run();
      return;
    }
    editor.chain().focus().toggleHeading({ level }).run();
  };

  const heading: HeadingDropdown = {
    currentLabel: vocab.headingLabels[activeHeadingLevel],
    disabled: !editor,
    options: vocab.headingLevels.map((level) => ({
      label: vocab.headingLabels[level],
      level,
      isActive: activeHeadingLevel === level,
      onClickAction: buildHeadingOnClick(level),
    })),
  };

  const divider: IconCommandItem = {
    label: 'Divider',
    title: vocab.blockTitles.divider,
    disabled: !editor,
    icon: Minus,
    onClickAction: () => editor?.chain().focus().setHorizontalRule().run(),
  };

  const inline: IconCommandSection = {
    items: [
      {
        label: 'Bold',
        title: vocab.inlineTitles.bold,
        disabled: !editor,
        isActive: editor?.isActive('bold'),
        icon: Bold,
        onClickAction: () => editor?.chain().focus().toggleBold().run(),
      },
      {
        label: 'Italic',
        title: vocab.inlineTitles.italic,
        disabled: !editor,
        isActive: editor?.isActive('italic'),
        icon: Italic,
        onClickAction: () => editor?.chain().focus().toggleItalic().run(),
      },
      {
        label: 'Inline code',
        title: vocab.inlineTitles.code,
        disabled: !editor,
        isActive: editor?.isActive('code'),
        icon: Code,
        onClickAction: () => editor?.chain().focus().toggleCode().run(),
      },
    ],
  };

  const list: IconCommandSection = {
    items: [
      {
        label: 'Bullet list',
        title: vocab.listTitles.bullet,
        disabled: !editor,
        isActive: editor?.isActive('bulletList'),
        icon: List,
        onClickAction: () => editor?.chain().focus().toggleBulletList().run(),
      },
      {
        label: 'Numbered list',
        title: vocab.listTitles.numbered,
        disabled: !editor,
        isActive: editor?.isActive('orderedList'),
        icon: ListOrdered,
        onClickAction: () => editor?.chain().focus().toggleOrderedList().run(),
      },
      {
        label: 'Checklist',
        title: vocab.listTitles.checklist,
        disabled: !editor,
        isActive: editor?.isActive('taskList'),
        icon: ListChecks,
        onClickAction: () => editor?.chain().focus().toggleTaskList().run(),
      },
    ],
  };

  const block: IconCommandSection = {
    items: [
      {
        label: 'Quote',
        title: vocab.blockTitles.quote,
        disabled: !editor,
        isActive: editor?.isActive('blockquote'),
        icon: Quote,
        onClickAction: () => editor?.chain().focus().toggleBlockquote().run(),
      },
      {
        label: 'Code block',
        title: vocab.blockTitles.codeBlock,
        disabled: !editor,
        isActive: editor?.isActive('codeBlock'),
        icon: Code2,
        onClickAction: () => editor?.chain().focus().toggleCodeBlock().run(),
      },
      {
        label: 'Table',
        title: vocab.blockTitles.table,
        disabled: !editor,
        icon: TableIcon,
        onClickAction: () => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
      },
      divider,
    ],
  };

  const link: IconCommandItem = {
    label: 'Link',
    title: vocab.linkTitle,
    disabled: !editor,
    isActive: editor?.isActive('link'),
    icon: LinkIcon,
    onClickAction: setLink,
  };

  const shared: SharedToolbar = { heading, divider, inline, list, block, link };

  if (variant === 'general') {
    return { variant: 'general', ...shared };
  }

  const markers: CommandSection = {
    label: 'Markers',
    items: [
      {
        label: '✅ Correct',
        title: 'Mark a correct example',
        disabled: !editor,
        onClickAction: () => editor?.chain().focus().insertContent('✅ ').run(),
      },
      {
        label: '❌ Incorrect',
        title: 'Mark an incorrect example',
        disabled: !editor,
        onClickAction: () => editor?.chain().focus().insertContent('❌ ').run(),
      },
      {
        label: '🚫 Forbidden',
        title: 'Mark forbidden content',
        disabled: !editor,
        onClickAction: () => editor?.chain().focus().insertContent('🚫 ').run(),
      },
      {
        label: '⚠️ Warning',
        title: 'Mark a warning/caution',
        disabled: !editor,
        onClickAction: () => editor?.chain().focus().insertContent('⚠️ ').run(),
      },
      {
        label: '📌 Summary',
        title: 'Mark a key summary',
        disabled: !editor,
        onClickAction: () => editor?.chain().focus().insertContent('📌 ').run(),
      },
      {
        label: '💡 Tip',
        title: 'Mark a tip/reference',
        disabled: !editor,
        onClickAction: () => editor?.chain().focus().insertContent('💡 ').run(),
      },
      {
        label: '🎯 Intent',
        title: 'Mark a goal/intent',
        disabled: !editor,
        onClickAction: () => editor?.chain().focus().insertContent('🎯 ').run(),
      },
    ],
  };

  return { variant: 'convention', ...shared, markers };
};
